import { useEffect, useState } from "react";
import { Shield, UserPlus, Trash2, Loader2, Lock, Edit3, X, Check, Mail, ShieldCheck, Eye, EyeOff, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "@/lib/constants";

export default function AdminAdmins() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const isFullAdmin = user?.role === "admin";

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/admins");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  return (
    <div data-testid="admin-admins-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-xl bg-[var(--sl-primary)]/10 grid place-items-center text-[var(--sl-primary)]"><Shield size={18} /></span>
          <div>
            <h1 className="font-heading text-2xl font-bold">Gérer les administrateurs</h1>
            <p className="text-sm text-gray-500">Créer/modifier les comptes admin, opérateur et lecture seule.</p>
          </div>
        </div>
        {isFullAdmin && (
          <button type="button" onClick={() => setShowCreate(true)} className="sl-btn-primary text-sm" data-testid="admins-create-btn">
            <UserPlus size={14} /> Nouvel administrateur
          </button>
        )}
      </div>

      {!isFullAdmin && (
        <div className="sl-card mb-4 border-amber-200 bg-amber-50 text-amber-800 text-sm" data-testid="viewer-banner">
          Vous êtes connecté en <span className="font-semibold">lecture seule</span>. Vous pouvez consulter les comptes mais pas les modifier.
        </div>
      )}

      {loading ? (
        <div className="sl-card text-center text-gray-500">Chargement…</div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="admins-grid">
          {items.map((a) => (
            <AdminRow
              key={a.id}
              admin={a}
              isMe={a.id === user?.id}
              canEdit={isFullAdmin}
              onChange={refresh}
            />
          ))}
        </div>
      )}

      {showCreate && (
        <CreateAdminModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); refresh(); }}
        />
      )}
    </div>
  );
}

function AdminRow({ admin, isMe, canEdit, onChange }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(admin.name || "");
  const [role, setRole] = useState(admin.role || "viewer");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [acting, setActing] = useState(null);

  const save = async () => {
    setActing("save");
    try {
      const payload = {};
      if (name !== admin.name) payload.name = name.trim();
      if (role !== admin.role) payload.role = role;
      if (password) payload.password = password;
      if (Object.keys(payload).length === 0) { setEditing(false); return; }
      await api.patch(`/admin/admins/${admin.id}`, payload);
      toast.success("Administrateur mis à jour");
      setPassword("");
      setEditing(false);
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setActing(null); }
  };

  const remove = async () => {
    if (!window.confirm(`Supprimer définitivement « ${admin.email} » ?`)) return;
    setActing("delete");
    try {
      await api.delete(`/admin/admins/${admin.id}`);
      toast.success("Administrateur supprimé");
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setActing(null); }
  };

  return (
    <article className="sl-card" data-testid={`admin-row-${admin.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="font-semibold text-base flex items-center gap-1.5 flex-wrap">
            {admin.name}
            {isMe && <span className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">Vous</span>}
            {admin.role === "admin" ? (
              <span className="inline-flex items-center gap-1 text-[10px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full font-semibold">
                <ShieldCheck size={10} /> Admin complet
              </span>
            ) : admin.role === "operator" ? (
              <span className="inline-flex items-center gap-1 text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-semibold">
                <ClipboardCheck size={10} /> Opérateur
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-semibold">
                <Eye size={10} /> Lecture seule
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500 flex items-center gap-1 mt-1"><Mail size={10} /> {admin.email}</div>
          <div className="text-[10px] text-gray-400 mt-0.5">Créé le {formatDate(admin.created_at)}</div>
        </div>
        {canEdit && !isMe && !editing && (
          <div className="flex flex-col items-end gap-1.5">
            <button type="button" onClick={() => setEditing(true)} className="text-xs text-[var(--sl-primary)] hover:underline inline-flex items-center gap-1" data-testid={`admin-edit-${admin.id}`}>
              <Edit3 size={11} /> Modifier
            </button>
            <button type="button" onClick={remove} disabled={!!acting} className="text-xs text-red-600 hover:underline inline-flex items-center gap-1" data-testid={`admin-delete-${admin.id}`}>
              {acting === "delete" ? <Loader2 size={10} className="animate-spin" /> : <Trash2 size={11} />} Supprimer
            </button>
          </div>
        )}
      </div>

      {editing && (
        <div className="mt-3 pt-3 border-t border-gray-100 space-y-3" data-testid={`admin-edit-form-${admin.id}`}>
          <div>
            <label className="sl-label">Nom</label>
            <input className="sl-input" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label className="sl-label">Rôle</label>
            <select className="sl-input" value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="viewer">Lecture seule (consultation uniquement)</option>
              <option value="operator">Opérateur (traite les opérations, sans réglages)</option>
              <option value="admin">Administrateur complet (peut tout modifier)</option>
            </select>
          </div>
          <div>
            <label className="sl-label flex items-center gap-1"><Lock size={11} /> Nouveau mot de passe <span className="text-xs text-gray-400 font-normal">(laisser vide pour conserver)</span></label>
            <div className="relative">
              <input className="sl-input pr-11" type={showPwd ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••" />
              <button type="button" onClick={() => setShowPwd((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700" aria-label={showPwd ? "Masquer" : "Afficher"}>
                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={() => { setEditing(false); setName(admin.name); setRole(admin.role); setPassword(""); }} className="text-xs inline-flex items-center gap-1 rounded-full border border-gray-200 text-gray-700 px-3 py-1.5 hover:bg-gray-50"><X size={11} /> Annuler</button>
            <button type="button" onClick={save} disabled={!!acting} className="text-xs inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white px-3 py-1.5 hover:bg-emerald-700 disabled:opacity-60" data-testid={`admin-save-${admin.id}`}>
              {acting === "save" ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />} Enregistrer
            </button>
          </div>
        </div>
      )}
    </article>
  );
}

function CreateAdminModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [role, setRole] = useState("viewer");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!name.trim() || !email.trim() || password.length < 6) {
      toast.error("Tous les champs sont obligatoires (mot de passe ≥ 6 caractères)");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/admin/admins", { name: name.trim(), email: email.trim().toLowerCase(), password, role });
      toast.success("Administrateur créé");
      onCreated?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} role="dialog" aria-modal="true" data-testid="admins-create-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-md w-full p-5 sm:p-6 max-h-[95vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-bold">Nouvel administrateur</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1" data-testid="admins-create-close"><X size={18} /></button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="sl-label">Nom</label>
            <input className="sl-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex. Agent Lomé" data-testid="admins-create-name" />
          </div>
          <div>
            <label className="sl-label">Email</label>
            <input className="sl-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="agent@swiftlink-africa.com" data-testid="admins-create-email" />
          </div>
          <div>
            <label className="sl-label">Mot de passe initial (≥ 6 caractères)</label>
            <div className="relative">
              <input className="sl-input pr-11" type={showPwd ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} minLength={6} data-testid="admins-create-password" />
              <button type="button" onClick={() => setShowPwd((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700" aria-label={showPwd ? "Masquer" : "Afficher"} data-testid="admins-create-password-toggle">
                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div>
            <label className="sl-label">Rôle</label>
            <select className="sl-input" value={role} onChange={(e) => setRole(e.target.value)} data-testid="admins-create-role">
              <option value="viewer">Lecture seule — peut consulter mais pas modifier</option>
              <option value="operator">Opérateur — traite les opérations en attente</option>
              <option value="admin">Administrateur complet — peut tout faire</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              <span className="font-semibold">Lecture seule</span> : consultation uniquement.<br />
              <span className="font-semibold">Opérateur</span> : peut traiter les transactions (changer le statut, preuves, commentaires, archiver) et modérer les avis, mais ne peut pas modifier les paramètres, ni gérer les agents/administrateurs.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} className="sl-btn-outline text-sm" data-testid="admins-create-cancel">Annuler</button>
          <button type="button" onClick={submit} disabled={submitting} className="sl-btn-primary text-sm disabled:opacity-60" data-testid="admins-create-submit">
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />} Créer
          </button>
        </div>
      </div>
    </div>
  );
}
